import { ReportParameters } from '@/components/report-components/report-parameters';
import { getEntityQueryOptions } from '@/queries/getEntityQueryOptions';
import type { EmailTemplate } from '@/models/email-template';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'
import { useState, useCallback, useEffect, useRef } from 'react';
import type { Condition } from '@/models/condition';
import { Button } from '@/components/ui/button';
import { DocumentPreview } from '@/components/document-editor/document-preview';
import { getDocumentTemplateMergeData } from '@/queries/getDocumentTemplateMergeData';
import LoadingSpinner from '@/components/spinner';

export const Route = createFileRoute(
  '/_app/$module_id/$entity_type_id/$entity_id/document/$templateId',
)({
  component: RouteComponent,
})


function RouteComponent() {
  const navigate = Route.useNavigate();
  const { templateId, entity_id, module_id, entity_type_id } = Route.useParams();
  const { data: result, isLoading } = useQuery(getEntityQueryOptions('document-templates', templateId));
  const template = result?.entity as EmailTemplate;

  const [parameters, setParameters] = useState<Record<string, Condition>>({});
  const [parametersInitialized, setParametersInitialized] = useState(false);
  const parametersRef = useRef<Record<string, Condition>>({});

  // Update ref when parameters change
  useEffect(() => {
    parametersRef.current = parameters;
  }, [parameters]);

  // Initialize when template is loaded and has no parameters
  useEffect(() => {
    if (template && !parametersInitialized) {
      if (!template.parameters || template.parameters.length === 0) {
        // No parameters needed, can initialize query immediately
        setParametersInitialized(true);
      }
    }
  }, [template, parametersInitialized]);

  const { data: mergeData, isLoading: mergeDataLoading } = useQuery({
    ...getDocumentTemplateMergeData(
      templateId,
      {
        entityTypeId: entity_type_id,
        entityId: entity_id,
        parameters: parameters,
      }
    ),
    enabled: parametersInitialized, // Only run when parameters have been properly initialized
  });

  const handleParametersChange = useCallback((newParameters: Record<string, Condition>) => {
    console.log('Parameters changed:', newParameters);

    const currentParameters = parametersRef.current;

    // Deep equality check using JSON comparison for more accurate comparison
    const currentJson = JSON.stringify(currentParameters);
    const newJson = JSON.stringify(newParameters);

    if (currentJson !== newJson) {
      console.log('Parameters actually changed, updating state');
      setParameters(newParameters);
    } else {
      console.log('Parameters are the same, skipping update');
    }

    if (!parametersInitialized) {
      setParametersInitialized(true);
    }
  }, []); // Empty dependency array since we use ref for current parameters

  const handlePreview = () => {
    // previewMutation.mutate();
  };


  if (isLoading) {
    return null;
  }

  return (
    <div className='flex flex-col w-[calc(100%-310px)] h-full'>
      <div className='p-4 border-b flex flex-col gap-4'>
        <h1 className='text-2xl text-blue-500 font-thin flex flex-row gap-2 items-center'>
          <svg className="h-7 w-7 text-blue-700 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
          {template?.name}
        </h1>
        <ReportParameters
          parameters={template?.parameters || []}
          onParametersChange={handleParametersChange}
        />
        <div className='flex flex-row gap-2 justify-end'>
          <Button
            onClick={() => {
              navigate({
                to: '/$module_id/$entity_type_id/$entity_id/overview',
                params: {
                  module_id: module_id,
                  entity_type_id: entity_type_id,
                  entity_id: entity_id,
                }
              })
            }}
            variant='outline'
            className='w-fit self-end'
          >
            Cancel
          </Button>
          <Button
            variant={'outline'}
            onClick={handlePreview}
            className='w-fit self-end'
          >
            Print
          </Button>
          <Button
            onClick={handlePreview}
            className='w-fit self-end'
          >
            Send Email
          </Button>
        </div>
      </div>
      {mergeDataLoading && <LoadingSpinner />}
      {!mergeDataLoading && mergeData && mergeData.length > 0 && <div className='flex-1 overflow-hidden'>
        <DocumentPreview
          bound_entity={mergeData[0]}
          blocks={template?.template ?? []}
        />
      </div>}
    </div>
  )
}
